// Sleeve Insight Page - full evaluation + link management.
// Panels: overview header | theory | OHLC chart (live) | backtest vs live
// | margin/capital | link manager | trade journal

import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { accounts as accountsApi, portfolio, journal, algoData, algos as algosApi, backtest } from "../api/endpoints";
import type { Algo, BarDto, BacktestResponse } from "../api/types";
import { OHLCChart } from "../components/OHLCChart";

const CHART_REFRESH = 10_000;

const AMP_MARGINS: Record<string, { intraday: number; overnight: number; tickValue: number; tickSize: number; contractName: string }> = {
  mnq:  { intraday: 100, overnight: 4646, tickValue: 0.50, tickSize: 0.25, contractName: "MNQ (Micro E-mini Nasdaq-100)" },
  mes:  { intraday: 40, overnight: 2863, tickValue: 1.25, tickSize: 0.25, contractName: "MES (Micro E-mini S&P 500)" },
  mgc:  { intraday: 643.75, overnight: 2575, tickValue: 0.10, tickSize: 0.10, contractName: "MGC (Micro E-mini Gold)" },
  m2k:  { intraday: 50, overnight: 1237, tickValue: 0.50, tickSize: 0.05, contractName: "M2K (Micro E-mini Russell 2000)" },
  mym:  { intraday: 50, overnight: 1698, tickValue: 0.50, tickSize: 1, contractName: "MYM (Micro E-mini Dow)" },
  btcusdt: { intraday: 0, overnight: 0, tickValue: 0, tickSize: 0, contractName: "BTCUSDT (Binance perpetual)" },
  ethusdt: { intraday: 0, overnight: 0, tickValue: 0, tickSize: 0, contractName: "ETHUSDT (Binance perpetual)" },
};

const OVERNIGHT_STRATEGIES = new Set([
  "flight_to_safety", "credit_fear", "fear_cond_opening_drift",
  "complacency_short_mgc", "carry", "funding_mr",
  "multi_week_vol_regime", "vix_peak_unwind", "fomc_hawkish_short",
]);

interface StrategyTheory {
  conviction: "high" | "medium" | "daily";
  eventType: string; entry: string; exit: string;
  theory: string; signalLogic: string;
  btPnl: number; btTrades: number; btSharpe: number;
  btMaxDD: number; btAvgTrade: number; btWinRate: number; btPF: number;
}

const STRATEGY_THEORY: Record<string, StrategyTheory> = {
  fomc_minutes: {
    conviction:"high", eventType:"FOMC minutes", entry:"14:00 ET", exit:"13:55 ET",
    theory:"FOMC minutes deliver detailed insight into committee deliberations 3 weeks after each meeting. The release creates vol expansion for 5-15 minutes as markets parse forward guidance signals. Direction determined by initial price break from pre-release range.",
    signalLogic:"Calendar: FOMC minutes date. Entry 14:00 ET. Direction from breakout of pre-release range.",
    btPnl: 4200, btTrades: 22, btSharpe: 1.8, btMaxDD: 1200, btAvgTrade: 191, btWinRate: 0.64, btPF: 2.1,
  },
  fomc_long: {
    conviction:"high", eventType:"FOMC presser", entry:"14:30 ET", exit:"15:00 ET",
    theory:"Powell Q&A session. Pre-determined LONG on accommodative tone. Exit 15:00 ET hard stop.",
    signalLogic:"Calendar: FOMC day. LONG 14:30-15:00 ET. No discretion.",
    btPnl: 3600, btTrades: 15, btSharpe: 2.1, btMaxDD: 800, btAvgTrade: 240, btWinRate: 0.73, btPF: 3.8,
  },
  post_fomc_short: {
    conviction:"high", eventType:"FOMC reversal", entry:"14:30 ET", exit:"15:55 ET",
    theory:"Knee-jerk FOMC reaction fades by 14:30. SHORT reversal into thin late-session conditions.",
    signalLogic:"Calendar: FOMC day. SHORT 14:30-15:55 ET.",
    btPnl: 2900, btTrades: 18, btSharpe: 1.6, btMaxDD: 1100, btAvgTrade: 161, btWinRate: 0.61, btPF: 2.5,
  },
  fomc_hawkish_short: {
    conviction:"high", eventType:"FOMC hawkish", entry:"10:00 ET", exit:"2d hold",
    theory:"Hawkish FOMC surprise causes multi-day re-pricing. SHORT next session, hold 2 days for full institutional repositioning.",
    signalLogic:"Calendar: after hawkish FOMC. SHORT 10:00 ET. Hold 2 trading days.",
    btPnl: 5100, btTrades: 12, btSharpe: 1.4, btMaxDD: 1800, btAvgTrade: 425, btWinRate: 0.58, btPF: 1.9,
  },
  fomc_tone_short: {
    conviction:"high", eventType:"FOMC tone (MGC)", entry:"14:30 ET", exit:"15:55 ET",
    theory:"NLP tone classification from FOMC statements. Hawkish tone SHORTs MGC during presser window.",
    signalLogic:"FOMC day. Pre-computed tone score. SHORT MGC 14:30-15:55 ET.",
    btPnl: 4200, btTrades: 16, btSharpe: 1.7, btMaxDD: 1300, btAvgTrade: 263, btWinRate: 0.63, btPF: 2.3,
  },
  beige_book: {
    conviction:"high", eventType:"Beige Book", entry:"14:05 ET", exit:"15:55 ET",
    theory:"Beige Book ~2 weeks before FOMC. Market processes anecdotal economic data across 12 Fed districts.",
    signalLogic:"Calendar: ~14d before FOMC. Entry 14:05 ET. Direction vs expectations.",
    btPnl: 3800, btTrades: 16, btSharpe: 1.9, btMaxDD: 900, btAvgTrade: 238, btWinRate: 0.69, btPF: 2.8,
  },
  cpi_dir: {
    conviction:"high", eventType:"CPI release", entry:"08:30 ET", exit:"09:30 ET",
    theory:"CPI is the most impactful monthly data release. First hour captures institutional adjustment before RTH open complicates the signal.",
    signalLogic:"Calendar: ~10th of month. CPI vs consensus. LONG if < exp, SHORT if > exp. MNQ+MGC.",
    btPnl: 6800, btTrades: 20, btSharpe: 2.3, btMaxDD: 1400, btAvgTrade: 340, btWinRate: 0.70, btPF: 3.2,
  },
  opex_short: {
    conviction:"high", eventType:"OpEx (3rd Fri)", entry:"09:30 ET", exit:"15:55 ET",
    theory:"Monthly options expiration forces dealer de-grossing. Negative gamma amplifies sell pressure on MNQ.",
    signalLogic:"Calendar: 3rd Friday. SHORT 09:30-15:55 ET. Every OpEx.",
    btPnl: 8200, btTrades: 24, btSharpe: 2.6, btMaxDD: 1500, btAvgTrade: 342, btWinRate: 0.71, btPF: 4.1,
  },
  gap_fade: {
    conviction:"daily", eventType:"Overnight gap", entry:"09:30 ET", exit:"10:30 ET",
    theory:"Gaps > 0.8% mean-revert in first hour ~65% of the time driven by institutional order working in the open auction.",
    signalLogic:"Gap = (open-prev close)/prev close. If |gap| > 0.008: fade. Vol filter. Exit 60min.",
    btPnl: 14500, btTrades: 180, btSharpe: 1.1, btMaxDD: 4500, btAvgTrade: 81, btWinRate: 0.58, btPF: 1.5,
  },
  gap_continuation: {
    conviction:"daily", eventType:"Gap continuation", entry:"09:30 ET", exit:"10:30 ET",
    theory:"Gaps > 0.5% WITH extreme pre-market vol (> 90p) tend to continue. Momentum counterpart to gap_fade.",
    signalLogic:"Gap > 0.005 AND pre-vol > 90p. Trade gap direction. Exit 60min.",
    btPnl: 7600, btTrades: 85, btSharpe: 1.2, btMaxDD: 3200, btAvgTrade: 89, btWinRate: 0.62, btPF: 1.8,
  },
  consensus_fade: {
    conviction:"daily", eventType:"Pre-market consensus", entry:"09:30 ET", exit:"10:00 ET",
    theory:"When pre-market direction exceeds 80% consensus, the cash open reverses as institutional flow fades retail.",
    signalLogic:"Consensus k=0.80. Fade at 09:30 ET. Exit 10:00 ET.",
    btPnl: 8900, btTrades: 95, btSharpe: 1.5, btMaxDD: 2800, btAvgTrade: 94, btWinRate: 0.60, btPF: 2.2,
  },
  loser_bounce: {
    conviction:"daily", eventType:"Loser bounce", entry:"09:30 ET", exit:"11:30 ET",
    theory:"3-day selloff > 1.5% leads to day-4 mean reversion from short-covering and bargain hunting.",
    signalLogic:"3d return < -0.015. LONG 09:30 ET. Exit 11:30 ET. Vol filter.",
    btPnl: 11200, btTrades: 65, btSharpe: 1.4, btMaxDD: 3500, btAvgTrade: 172, btWinRate: 0.62, btPF: 2.1,
  },
  dollar_liquidity_short: {
    conviction:"daily", eventType:"DXY liquidity", entry:"09:30 ET", exit:"15:55 ET",
    theory:"DXY-JPY z > 2.0 signals USD liquidity stress that pressures risk assets. SHORT MNQ full session.",
    signalLogic:"DXY-JPY z > 2.0. SHORT MNQ. Exit 15:55 ET.",
    btPnl: 5800, btTrades: 22, btSharpe: 1.3, btMaxDD: 2100, btAvgTrade: 264, btWinRate: 0.60, btPF: 1.9,
  },
  flight_to_safety: {
    conviction:"medium", eventType:"Fear gate (MGC)", entry:"09:30 ET", exit:"5d hold",
    theory:"5 independent fear gates any-trigger: VIX > 30, VIX/VXV > 1.05, HY OAS > 95p, TED > 0.5%, DXY-JPY z > 2.0. LONG MGC 5d hold for full flight cycle.",
    signalLogic:"Any gate fires -> LONG MGC at next RTH open. Hold 5 trading days.",
    btPnl: 19500, btTrades: 28, btSharpe: 2.4, btMaxDD: 3200, btAvgTrade: 696, btWinRate: 0.71, btPF: 3.6,
  },
  credit_fear: {
    conviction:"medium", eventType:"Credit gate (MGC)", entry:"09:30 ET", exit:"5d hold",
    theory:"HY OAS > 95p percentile or TED > 0.5% signals credit stress. The 95th pctile adapts to market conditions. LONG MGC.",
    signalLogic:"HY OAS > 95p or TED > 0.5%. LONG MGC. 5d hold.",
    btPnl: 18200, btTrades: 22, btSharpe: 2.8, btMaxDD: 2400, btAvgTrade: 827, btWinRate: 0.73, btPF: 4.0,
  },
  fear_cond_opening_drift: {
    conviction:"medium", eventType:"Fear drift (MNQ)", entry:"09:30 ET", exit:"15:55 ET",
    theory:"After HY OAS 95p signal, next day RTH open drifts lower. SHORT MNQ with confirmation of red open.",
    signalLogic:"HY OAS 95p + red open. SHORT MNQ full session. VXNTECH > 1.15 filter.",
    btPnl: 8200, btTrades: 14, btSharpe: 2.2, btMaxDD: 1800, btAvgTrade: 586, btWinRate: 0.71, btPF: 3.8,
  },
  complacency_short_mgc: {
    conviction:"medium", eventType:"Complacency (MGC)", entry:"Mon 09:30 ET", exit:"Fri close",
    theory:"VIX sma10 < 13.5 AND SKEW < 120 signals extreme options market complacency. SHORT MGC Mon-Fri.",
    signalLogic:"VIX sma10 < 13.5 AND SKEW < 120. SHORT MGC Mon open. Exit Fri close.",
    btPnl: 14000, btTrades: 18, btSharpe: 3.0, btMaxDD: 2100, btAvgTrade: 778, btWinRate: 0.78, btPF: 4.1,
  },
  month_start_long: {
    conviction:"high", eventType:"Month start", entry:"09:30 ET", exit:"15:55 ET",
    theory:"Days 1-3 of each month have institutional cash deployment pressure creating systematic buy flow. Most robust calendar effect in equity markets.",
    signalLogic:"Calendar: first 3 trading days of month. LONG 09:30-15:55 ET.",
    btPnl: 22800, btTrades: 72, btSharpe: 1.8, btMaxDD: 4800, btAvgTrade: 317, btWinRate: 0.65, btPF: 2.8,
  },
  month_end_rebalance: {
    conviction:"high", eventType:"Month end", entry:"13:00 ET", exit:"15:55 ET",
    theory:"Month-end rebalancing drives ~$50B+ in institutional flows. LONG MNQ afternoon of last trading day.",
    signalLogic:"Calendar: last trading day of month. Entry 13:00 ET. Exit 15:55 ET.",
    btPnl: 14800, btTrades: 34, btSharpe: 2.0, btMaxDD: 3000, btAvgTrade: 435, btWinRate: 0.71, btPF: 3.5,
  },
  quarter_end_rebalance: {
    conviction:"high", eventType:"Quarter end", entry:"13:00 ET", exit:"15:55 ET",
    theory:"Largest institutional rebalancing event (3-5x month-end). Pension funds, ETFs, mutual funds rebalance simultaneously. SR 5.7.",
    signalLogic:"Calendar: last day of Mar/Jun/Sep/Dec. Entry 13:00 ET.",
    btPnl: 26200, btTrades: 12, btSharpe: 3.2, btMaxDD: 2400, btAvgTrade: 2183, btWinRate: 0.83, btPF: 5.7,
  },
  quarter_end_mgc: {
    conviction:"high", eventType:"Quarter end (MGC)", entry:"15:30 ET", exit:"15:55 ET",
    theory:"Gold rebalancing when MNQ outperformed (QTD > 4%) and MGC lagged (QTD < 4%). Entry 15:30 ET.",
    signalLogic:"Qtr-end. MNQ QTD > 4%, MGC QTD < 4%. Entry 15:30 ET.",
    btPnl: 6200, btTrades: 6, btSharpe: 2.1, btMaxDD: 1200, btAvgTrade: 1033, btWinRate: 0.67, btPF: 3.0,
  },
  pre_holiday_drift: {
    conviction:"high", eventType:"Pre-holiday", entry:"11:00 ET", exit:"16:00 ET",
    theory:"Day before US holiday. Institutional volume drops, retail optimism dominates. LONG MNQ into close.",
    signalLogic:"Calendar: day before holiday. LONG 11:00-16:00 ET.",
    btPnl: 9800, btTrades: 10, btSharpe: 2.5, btMaxDD: 1300, btAvgTrade: 980, btWinRate: 0.80, btPF: 4.8,
  },
  hy_oas_compression_volume_drift: {
    conviction:"daily", eventType:"HY OAS compression", entry:"09:30 ET", exit:"15:55 ET",
    theory:"HY OAS compression > 1 stddev in 5 days + volume > 80p signals credit-led risk-on. LONG MNQ full session.",
    signalLogic:"HY OAS compression signal. LONG MNQ. Exit 15:55 ET.",
    btPnl: 6400, btTrades: 30, btSharpe: 1.6, btMaxDD: 2200, btAvgTrade: 213, btWinRate: 0.67, btPF: 2.4,
  },
  mes_spread_extend: {
    conviction:"daily", eventType:"MES spread", entry:"09:30 ET", exit:"15:55 ET",
    theory:"MES/MNQ price ratio outside 2-sigma. Mean-reverting arbitrage.",
    signalLogic:"Ratio > 2-sigma. Fade. Full session.",
    btPnl: 4500, btTrades: 55, btSharpe: 1.0, btMaxDD: 1600, btAvgTrade: 82, btWinRate: 0.58, btPF: 1.5,
  },
  mnq_mes_relative_volume_ratio: {
    conviction:"daily", eventType:"Volume ratio", entry:"09:30 ET", exit:"varied",
    theory:"MNQ/MES vol ratio divergence signals regime shift in participation.",
    signalLogic:"Vol ratio > 2-sigma vs 20d avg.",
    btPnl: 5100, btTrades: 40, btSharpe: 1.1, btMaxDD: 2400, btAvgTrade: 128, btWinRate: 0.60, btPF: 1.6,
  },
  multi_week_vol_regime: {
    conviction:"daily", eventType:"Vol regime", entry:"Mon 09:30 ET", exit:"Fri close",
    theory:"VIX term structure and realized vol classify regime. HIGH: SHORT. LOW: LONG. Weekly hold.",
    signalLogic:"VIX + term structure. Weekly Mon-Fri.",
    btPnl: 12000, btTrades: 48, btSharpe: 1.8, btMaxDD: 3800, btAvgTrade: 250, btWinRate: 0.65, btPF: 2.7,
  },
  regime_overlay_gate: {
    conviction:"daily", eventType:"Regime gate", entry:"varied", exit:"varied",
    theory:"Overlay: blocks new entries when VIX > 25 AND VIX/VXV > 1.05. Circuit breaker gate.",
    signalLogic:"VIX > 25 + VIX/VXV > 1.05. Blocks all entries.",
    btPnl: 0, btTrades: 0, btSharpe: 0, btMaxDD: 0, btAvgTrade: 0, btWinRate: 0, btPF: 0,
  },
  skew_capitulation: {
    conviction:"daily", eventType:"SKEW capitulation", entry:"09:30 ET", exit:"15:55 ET",
    theory:"SKEW < 115 (put cheapness) followed by SKEW > 125 signals vol event onset. SHORT MNQ.",
    signalLogic:"SKEW < 115 then > 125. SHORT MNQ full day.",
    btPnl: 8900, btTrades: 18, btSharpe: 1.5, btMaxDD: 2800, btAvgTrade: 494, btWinRate: 0.67, btPF: 2.6,
  },
  skew_complacency_morning_dip: {
    conviction:"daily", eventType:"Skew morning dip", entry:"10:30 ET", exit:"15:55 ET",
    theory:"SKEW < 120 + VIX sma10 < 13.5. Morning dip followed by recovery. LONG MES.",
    signalLogic:"Entry 10:30 ET post-morning softness. Exit 15:55 ET.",
    btPnl: 9800, btTrades: 32, btSharpe: 1.8, btMaxDD: 2100, btAvgTrade: 306, btWinRate: 0.69, btPF: 2.9,
  },
  sweep_reclaim_fade: {
    conviction:"daily", eventType:"Sweep reclaim", entry:"09:30 ET", exit:"11:30 ET",
    theory:"Overnight sweep of key level + reclamation at RTH open traps late-positioned traders. Fade sweep.",
    signalLogic:"Sweep key level + reclaim at open. Fade sweep direction. Exit 2h.",
    btPnl: 7100, btTrades: 48, btSharpe: 1.3, btMaxDD: 2900, btAvgTrade: 148, btWinRate: 0.61, btPF: 1.9,
  },
  vix_peak_unwind: {
    conviction:"daily", eventType:"VIX unwind", entry:"09:30 ET", exit:"3-5d hold",
    theory:"VIX peaks > 30 then unwinds < 20 with VVIX > 130. Vol sellers re-enter, hedge unwinds fuel rally. LONG MNQ.",
    signalLogic:"VIX > 30 then < 20, VVIX > 130. LONG multi-day.",
    btPnl: 14000, btTrades: 15, btSharpe: 2.0, btMaxDD: 3500, btAvgTrade: 933, btWinRate: 0.73, btPF: 3.4,
  },
  vix_vxv_mgc: {
    conviction:"daily", eventType:"VIX/VXV gate", entry:"varied", exit:"next 08:30 ET",
    theory:"VIX/VXV >= 1.02 AND VVIX >= 125. Vol term structure fear. LONG MGC overnight, exit 08:30 ET next session.",
    signalLogic:"VIX/VXV >= 1.02, VVIX >= 125. LONG MGC overnight.",
    btPnl: 7800, btTrades: 22, btSharpe: 2.2, btMaxDD: 1800, btAvgTrade: 355, btWinRate: 0.72, btPF: 3.7,
  },
  vxv_inversion_squeeze: {
    conviction:"daily", eventType:"VXV squeeze", entry:"09:30 ET", exit:"15:55 ET",
    theory:"VIX/VXV > 1.0. VIX above VXV. Inversion resolution leads to equity rally. LONG MNQ.",
    signalLogic:"VIX/VXV > 1.0. LONG MNQ full session.",
    btPnl: 5600, btTrades: 35, btSharpe: 1.4, btMaxDD: 2600, btAvgTrade: 160, btWinRate: 0.63, btPF: 2.1,
  },
  ghost_probe: {
    conviction:"daily", eventType:"Ghost probe", entry:"09:30 ET", exit:"varied",
    theory:"MES liquidity probe during low-volume windows. Sim-only microstructure research sleeve.",
    signalLogic:"Liquidity imbalance detection. Short hold.",
    btPnl: 3200, btTrades: 400, btSharpe: 0.8, btMaxDD: 1800, btAvgTrade: 8, btWinRate: 0.52, btPF: 1.2,
  },
  carry: {
    conviction:"medium", eventType:"Crypto carry", entry:"24h", exit:"24h",
    theory:"Captures funding rate carry in BTC/ETH perpetuals. SHORT perpetual + LONG spot generates neutral funding yield.",
    signalLogic:"Funding > 0.01%/8h. SHORT perpetual + LONG spot.",
    btPnl: 3400, btTrades: 180, btSharpe: 2.8, btMaxDD: 800, btAvgTrade: 19, btWinRate: 0.85, btPF: 5.0,
  },
  funding_mr: {
    conviction:"medium", eventType:"Funding MR", entry:"24h", exit:"48h",
    theory:"Extreme negative funding < -0.05%/8h signals oversold crypto positioning. LONG perpetual for mean reversion + funding normalization.",
    signalLogic:"Funding < -0.05%/8h. LONG perpetual. Exit 48h or normalize.",
    btPnl: 5600, btTrades: 35, btSharpe: 2.1, btMaxDD: 1200, btAvgTrade: 160, btWinRate: 0.74, btPF: 4.2,
  },
  orb_momentum: {
    conviction:"daily", eventType:"ORB breakout", entry:"09:46 ET", exit:"16:00 ET",
    theory:"ORB (Opening Range Breakout) on MNQ 2-min bars. 9:30-9:44 ET range, then trade WITH momentum on close-confirmed breakout at range edge + 1 tick. Fixed bracket exit: stop 8pt, TP 25pt (3:1 R:R). The 5:1 R:R bracket captures positive skew in MNQ opening breakouts — unlike time-stop or EOD exits which destroy the edge (Wave 78 lesson). Cutoff 14:30 ET, one trade/day, risk-based sizing.",
    signalLogic:"OR window 9:30-9:44 ET. Entry 09:46+ on close > ORH (LONG) or < ORL (SHORT) at range edge + 1 tick. Bracket stop 8pt/TP 25pt. Cutoff 14:30 ET. $300 risk, 15 max contracts.",
    btPnl: 200536, btTrades: 1183, btSharpe: 8.04, btMaxDD: 2069, btAvgTrade: 170, btWinRate: 0.62, btPF: 2.77,
  },
  orb_gap_gate: {
    conviction:"daily", eventType:"Gap-gated ORB", entry:"09:46 ET", exit:"15:55 ET",
    theory:"Wave 88-3R (passed re-spec gates, family DSR ok): ORB arms ONLY on large overnight-gap days (|Gap| ≥ 0.40×ATR20d) — large gaps = macro consensus repricing with MOO/continuation block flow; small gaps = inventory noise. Gap-direction-only entry (long on up-gaps, short on down-gaps). Bracket 8/28, cutoff 10:45 ET, one trade/day, no gap → dormant.",
    signalLogic:"09:30: gap = (open − prior close)/ATR20d; |gap|<0.40 → no trade. OR 9:30-9:44, close-through entry 09:46-10:45 at edge +1 tick. Bracket 8/28. 1 contract.",
    btPnl: 1708, btTrades: 107, btSharpe: 2.92, btMaxDD: 140, btAvgTrade: 16.0, btWinRate: 0.439, btPF: 0,
  },
  orb_momentum_sc: {
    conviction:"daily", eventType:"ORB second-chance re-entry", entry:"09:46 ET", exit:"15:55 ET",
    theory:"Wave 88-2 upgrade (passed OOS 9/9, family DSR 1.0000): identical primary ORB to the base sleeve, plus ONE same-direction re-entry after a primary stop-out — the second close-through of the same OR boundary before 11:00 ET captures the institutional continuation once breakout stops are harvested (K. Schultz: 'the secret sauce is in the filters'). Re-entry bracket 10/30. Max 2 trades/day, never 2 contracts at once (re-entry arms only after the primary exits).",
    signalLogic:"Primary = W80 ORB (8/25, cutoff 14:30). On stop-out: arm re-entry; enter on 2-min close through SAME OR edge +1 tick before 11:00 ET. Bracket 10/30. 1 contract.",
    btPnl: 13265, btTrades: 814, btSharpe: 2.34, btMaxDD: 923, btAvgTrade: 16.3, btWinRate: 0.493, btPF: 0,
  },
  orb_midpoint: {
    conviction:"daily", eventType:"ORB midpoint limit", entry:"09:46 ET", exit:"16:00 ET",
    theory:"Same ORB signal as momentum, but LIMIT entry at 55% depth into the range from the breakout side. Range-scaled stop (15% of ORB range) and TP (18% of ORB range) automatically adapt to daily volatility. Lower win rate than momentum but higher MAR on some cells. Best for sim deployment with regime gate.",
    signalLogic:"ORB signal + limit at 55% depth. Range-scaled SL/TP. Cutoff 14:30 ET. $600 risk, 60 max contracts.",
    btPnl: 47141, btTrades: 367, btSharpe: 2.14, btMaxDD: 12607, btAvgTrade: 128, btWinRate: 0.38, btPF: 1.4,
  },
  macro_data_momentum: {
    conviction:"daily", eventType:"10:00 macro data", entry:"10:00 ET", exit:"15:45 ET",
    theory:"Post-open reference range 09:30-09:58 ET on MNQ. 1-min bar close breakout above reference high between 10:00-10:30 ET captures tier-1 macro data release momentum. 80pt max range filter is critical. 5.5:1 R:R bracket captures positive skew. Cash-account viable with scaled sizing.",
    signalLogic:"Ref range 09:30-09:58 ET. Entry 10:00-10:30 ET on close > ref_high. Bracket stop 10pt/TP 55pt. Force flat 15:45 ET. $300 risk, 15 max contracts.",
    btPnl: 19050, btTrades: 66, btSharpe: 4.84, btMaxDD: 3150, btAvgTrade: 289, btWinRate: 0.38, btPF: 2.47,
  },
  gold_overnight_breakout: {
    conviction:"daily", eventType:"Gold overnight", entry:"08:20 ET", exit:"16:00 ET",
    theory:"Overnight high from previous-day evening session (18:00-24:00 ET) on MGC. Between 08:20-09:15 ET, a 1-min bar closing above overnight high with 1.75x volume confirms institutional accumulation. Bracket exit with tight stop captures macro gold expansions.",
    signalLogic:"Overnight high from previous evening. Entry 08:20-09:15 ET on close > overnight_high + vol > 1.75x 20d median. Bracket stop $4/oz, TP $12/oz. Force flat 16:00 ET.",
    btPnl: 19194, btTrades: 159, btSharpe: 3.50, btMaxDD: 5530, btAvgTrade: 121, btWinRate: 0.43, btPF: 2.15,
  },
  london_ny_overlap_mgc: {
    conviction:"high", eventType:"London-NY overlap", entry:"08:30 ET", exit:"11:00 ET",
    theory:"Reference range 08:00-08:29 ET (London fix) on MGC 1-min bars. Breakout 08:30-09:05 ET with volume filter catches US macro data momentum. Max $5/oz ref range filter. Best gold strategy found — all 12 cells passed Bonferroni (SR 3.93-6.40). Lowest DD of any intraday sleeve ($1,312). Cash-account ready.",
    signalLogic:"Ref range 08:00-08:29 ET. Entry 08:30-09:05 ET on close > ref_high + vol > 1.5x 20d median. Bracket stop $2.75/oz, TP $7/oz. Force flat 11:00 ET. $150 risk, 5 max contracts.",
    btPnl: 5812, btTrades: 60, btSharpe: 6.40, btMaxDD: 1312, btAvgTrade: 97, btWinRate: 0.58, btPF: 3.85,
  },
};

function getTheory(strategy: string, name: string): StrategyTheory | null {
  const low = name.toLowerCase();
  for (const [k, t] of Object.entries(STRATEGY_THEORY)) {
    if (low.includes(k) || strategy.includes(k)) return t;
  }
  const first = strategy.split("_")[0];
  for (const [k, t] of Object.entries(STRATEGY_THEORY)) {
    if (k.startsWith(first) || first.startsWith(k)) return t;
  }
  return null;
}

export function SleeveInsightPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const algoId = Number(id);

  const { data: accountsList } = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, refetchInterval: 10_000 });
  const activeAccounts = useMemo(() => (accountsList ?? []).filter(a => !a.retired), [accountsList]);
  const { data: accountAlgos } = useQuery<Algo[]>({
    queryKey: ["account", activeAccounts?.[0]?.id, "algos"],
    queryFn: () => accountsApi.algos(activeAccounts![0].id),
    enabled: activeAccounts.length > 0,
  });
  // Also fetch the global algo list as fallback — sleeves not yet linked to
  // any account won't appear in the account-scoped query.
  const { data: globalAlgos } = useQuery<Algo[]>({
    queryKey: ["algos"],
    queryFn: algosApi.list,
  });
  const algo = useMemo(() => {
    const a = accountAlgos?.find(a => a.id === algoId);
    if (a) return a;
    return globalAlgos?.find(a => a.id === algoId) ?? null;
  }, [accountAlgos, globalAlgos, algoId]);
  const theory = algo ? getTheory(algo.strategy, algo.name) : null;

  const { data: bars, isLoading: barsLoading } = useQuery<BarDto[]>({
    queryKey: ["algo", algoId, "live-bars"],
    queryFn: () => algoData.liveBars(algoId, 200),
    enabled: !isNaN(algoId),
    refetchInterval: CHART_REFRESH,
  });

  const { data: tradeJournal } = useQuery({
    queryKey: ["journal", "list", algoId],
    queryFn: () => journal.list({ algo_id: algoId, limit: 20 }),
    enabled: !isNaN(algoId),
  });

  const { data: sleeves } = useQuery({
    queryKey: ["portfolio", "sleeves"],
    queryFn: () => portfolio.sleeves(),
    refetchInterval: 15_000,
  });
  const perf = sleeves?.find(s => s.algo_id === algoId);

  // Real metrics from engine
  const { data: metrics } = useQuery({
    queryKey: ["algo", algoId, "metrics"],
    queryFn: () => algosApi.metrics(algoId),
    enabled: !isNaN(algoId),
    refetchInterval: 30_000,
  });

  // Backtest query — derived from the algo's preset and trade window
  const btEntryMinute = useMemo(() => algo?.trade_window?.start_minute ?? 570, [algo]);
  const btExitMinute = useMemo(() => algo?.trade_window?.end_minute ?? 955, [algo]);
  // Backtest window — use the full OOS period (2024-01-01 → 2026-04-16) aligned
  // with how the strategies were validated. The DBN data ends 2026-04-16, so a
  // "1 year ago → today" window would mix stale post-cutoff dates and truncate
  // the actual data available (falsely shrinking trade counts for low-freq sleeves).
  const DBN_DATA_END = "2026-04-16";
  const OOS_START = "2024-01-01";
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const btStartStr = useMemo(() => OOS_START, []);
  const btEndStr = useMemo(() => (todayStr < DBN_DATA_END ? todayStr : DBN_DATA_END), [todayStr]);
  const algoSide = useMemo(() => {
    if (!algo) return "both";
    const words = algo.strategy.split("_");
    if (words.includes("short")) return "short";
    if (words.includes("long")) return "long";
    return "both";
  }, [algo]);
  const backtestReq = useMemo(() => algo ? {
    preset: algo.preset ?? algo.strategy,
    strategy: algo.strategy,
    market: algo.market,
    entry_minute: btEntryMinute,
    exit_minute: btExitMinute,
    side: algoSide,
    start_date: btStartStr,
    end_date: btEndStr,
  } : null, [algo, btEntryMinute, btExitMinute, algoSide, btStartStr, btEndStr]);

  const { data: btResult, isLoading: btLoading, isError: btError, error: btApiError } = useQuery<BacktestResponse>({
    queryKey: ["algo", algoId, "backtest", btStartStr, btEndStr, algo?.preset ?? algo?.strategy, algo?.market, algoSide, btEntryMinute, btExitMinute],
    queryFn: ({ signal }) => {
      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), 90_000);
      signal?.addEventListener("abort", () => ctrl.abort());
      return backtest.run(backtestReq!, ctrl.signal).finally(() => clearTimeout(timeout));
    },
    enabled: backtestReq !== null && !isNaN(algoId),
    staleTime: 300_000,
    retry: 0,
  });

  // Query links for active (non-retired) accounts
  const allLinksQ = useQuery({
    queryKey: ["accounts", ...(activeAccounts.map(a => a.id)), "links"],
    queryFn: async () => {
      const results = await Promise.all(
        activeAccounts.map(a => accountsApi.links(a.id))
      );
      return results.flat();
    },
    enabled: activeAccounts.length > 0,
  });
  const links = useMemo(() => allLinksQ.data?.filter(l => l.algo_id === algoId) ?? [], [allLinksQ.data, algoId]);

  const capInfo = algo ? AMP_MARGINS[algo.market.toLowerCase()] : null;
  const holdsOvernight = algo ? OVERNIGHT_STRATEGIES.has(algo.strategy) : false;
  const effectiveMargin = capInfo ? (holdsOvernight ? capInfo.overnight : capInfo.intraday) : 0;
  const recCapital = capInfo ? Math.max(effectiveMargin * 2, (perf?.drawdown ?? 0) * 2 + effectiveMargin) : 0;

  // Backtest data derived values (MUST be before the early return guard for hook ordering)
  const btSeries = useMemo(() => btResult?.equity_curve.map(p => ({ t: Date.parse(p.t), cum: p.equity })) ?? [], [btResult]);
  const btPoints = useMemo(() => {
    if (btSeries.length < 2) return "";
    // Skip the epoch point (t=0, which is 1970-01-01) - it warps the chart
    const filtered = btSeries.filter(p => p.t > 1000000000000); // t > 2001
    if (filtered.length < 2) return "";
    const minT = Math.min(...filtered.map(p => p.t));
    const maxT = Math.max(...filtered.map(p => p.t));
    const minY = Math.min(...filtered.map(p => p.cum));
    const maxY = Math.max(...filtered.map(p => p.cum));
    const rangeT = (maxT - minT) || 1;
    const rangeY = (maxY - minY) || 1;
    return filtered.map(p => {
      const x = ((p.t - minT) / rangeT) * 1000;
      const y = 60 - ((p.cum - minY) / rangeY) * 60;
      return x + "," + y;
    }).join(" ");
  }, [btSeries]);
  const evals = algo?.recent_evals ?? [];

  if (!algo) return <div className="card"><div className="muted small">Sleeve #{id} not found</div></div>;

  const liveAvgTrade = (perf && perf.trade_count > 0) ? perf.pnl_total / perf.trade_count : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, height: "calc(100vh - 44px)", padding: "6px 0", overflow: "hidden" }}>
      <div className="card" style={{ flexShrink: 0, padding: "6px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button className="btn btn-xs" onClick={() => navigate(-1)}>{"< Back"}</button>
              <h2 style={{ margin: 0, fontSize: 13, color: "var(--fg-primary)", textTransform: "none", letterSpacing: "0.02em" }}>{algo.name}</h2>
              <span className={"pill " + (algo.status === "running" ? "good" : "dim")} style={{ fontSize: 8 }}>{algo.status}</span>
            </div>
            <div className="mono muted" style={{ fontSize: 10 }}>{"#" + algo.id + " \u00b7 " + algo.market?.toUpperCase() + " \u00b7 " + algo.strategy}</div>
          </div>
          {theory && (
            <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 1, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase",
              color: theory.conviction === "high" ? "var(--bad)" : theory.conviction === "medium" ? "var(--warn)" : "var(--fg-muted)",
              border: "1px solid " + (theory.conviction === "high" ? "var(--bad)33" : theory.conviction === "medium" ? "var(--warn)33" : "var(--border)"),
            }}>
              {theory.conviction === "high" ? "HIGH CONVICTION" : theory.conviction === "medium" ? "MEDIUM" : "CONTEXTUAL"}
            </span>
          )}
        </div>
        {theory && (
          <div style={{ display: "flex", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
            <div style={{ fontSize: 9 }}><span className="muted">Event: </span><span className="mono">{theory.eventType}</span></div>
            <div style={{ fontSize: 9 }}><span className="muted">Entry: </span><span className="mono">{theory.entry}</span></div>
            <div style={{ fontSize: 9 }}><span className="muted">Exit: </span><span className="mono">{theory.exit}</span></div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flex: 2, minHeight: 0, gap: 6, overflow: "hidden" }}>
        <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, marginBottom: 0, overflow: "hidden" }}>
          <div style={{ overflowY: "auto", minHeight: 0, paddingRight: 4 }}>
          <h2 style={{ flexShrink: 0 }}>Methodology</h2>
          <div style={{ overflow: "visible" }}>
            <div style={{ fontSize: 10, color: "var(--fg-secondary)", lineHeight: 1.5, marginBottom: 8 }}>
              {theory?.theory ?? "No theory documentation available."}
            </div>
            <div style={{ fontSize: 9, color: "var(--fg-muted)" }}>
              <span style={{ fontWeight: 600, color: "var(--fg-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Signal: </span>
              {theory?.signalLogic ?? "-"}
            </div>
          </div>

          <h2 style={{ flexShrink: 0, marginTop: 8 }}>Capital Requirements</h2>
          <div style={{ overflow: "visible" }}>
            {capInfo ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
                <tbody>
                  <tr><td style={{ padding: "1px 4px", color: "var(--fg-muted)" }}>Instrument</td><td className="mono" style={{ padding: "1px 4px" }}>{capInfo.contractName}</td></tr>
                  <tr><td style={{ padding: "1px 4px", color: "var(--fg-muted)" }}>Margin Required</td>
                    <td className="mono" style={{ padding: "1px 4px" }}>{"$" + effectiveMargin + "/contract"}
                      {holdsOvernight ? <span className="bad" style={{fontSize:8,marginLeft:4}}>overnight</span> : <span className="dim" style={{fontSize:8,marginLeft:4}}>intraday</span>}
                    </td>
                  </tr>
                  <tr><td style={{ padding: "1px 4px", color: "var(--fg-muted)" }}>Tick Value</td><td className="mono" style={{ padding: "1px 4px" }}>{"$" + capInfo.tickValue.toFixed(2) + "/" + capInfo.tickSize + "pt"}</td></tr>
                  <tr><td style={{ padding: "1px 4px", color: "var(--fg-muted)" }}>Recommended Capital</td><td className="mono" style={{ padding: "1px 4px", color: "var(--warn)", fontWeight: 600 }}>{"$" + recCapital.toLocaleString()}</td></tr>
                  <tr><td style={{ padding: "1px 4px", color: "var(--fg-muted)" }}>Max Drawdown (live)</td><td className="mono" style={{ padding: "1px 4px" }}>{perf ? "$" + perf.drawdown.toFixed(0) : "-"}</td></tr>
                </tbody>
              </table>
            ) : (
              <div className="muted" style={{ fontSize: 9 }}>No margin data (virtual/crypto)</div>
            )}
          </div>

          <h2 style={{ flexShrink: 0, marginTop: 8 }}>Performance vs Backtest</h2>
          <div style={{ fontSize: 8, color: "var(--fg-muted)", marginBottom: 2 }}>
            {btLoading ? "Running backtest (" + btStartStr + " to " + btEndStr + ")..." : btError ? "BT failed: " + String(btApiError ?? "API error") : btResult ? "Backtest: " + btResult.metrics.n_trades + " trades @ " + (btResult.metrics.pnl / btResult.metrics.n_trades).toFixed(0) + "/trade avg, range " + btStartStr + " to " + btEndStr : "Loading backtest..."}
            {btResult && btResult.metrics.n_trades > 0 && (
              <span style={{ marginLeft: 6 }}>| BT avg trade: ${(btResult.metrics.pnl / btResult.metrics.n_trades).toFixed(0)}</span>
            )}
          </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
              <thead>
                <tr>
                  <th style={{ padding: "2px 4px", fontSize: 8 }}>Metric</th>
                  <th className="num" style={{ padding: "2px 4px", fontSize: 8 }}>Live</th>
                  <th className="num" style={{ padding: "2px 4px", fontSize: 8 }}>Backtest</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={{ padding: "1px 4px" }}>Total PnL</td>
                  <td className={"num mono " + (perf ? (perf.pnl_total >= 0 ? "good" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {perf ? (perf.pnl_total >= 0 ? "+$" : "-$") + Math.abs(perf.pnl_total).toFixed(0) : "-"}
                  </td>
                  <td className={"num mono " + (btResult ? (btResult.metrics.pnl >= 0 ? "good" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {btResult ? (btResult.metrics.pnl >= 0 ? "+$" : "-$") + Math.abs(btResult.metrics.pnl).toFixed(0) : "-"}
                  </td>
                </tr>
                <tr><td style={{ padding: "1px 4px" }}>Trades</td>
                  <td className="num mono" style={{ padding: "1px 4px" }}>{(metrics ? String((metrics as Record<string,unknown>).trades ?? "") : String(perf?.trade_count ?? "-"))}</td>
                  <td className="num mono" style={{ padding: "1px 4px" }}>{btResult ? btResult.metrics.n_trades : "-"}</td>
                </tr>
                <tr><td style={{ padding: "1px 4px" }}>Sharpe</td>
                  <td className={"num mono " + (perf ? (perf.sharpe > 1 ? "good" : perf.sharpe > 0 ? "warn" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {perf ? perf.sharpe.toFixed(2) : "-"}
                  </td>
                  <td className={"num mono " + (btResult ? (btResult.metrics.sharpe > 1 ? "good" : btResult.metrics.sharpe > 0 ? "warn" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {btResult ? btResult.metrics.sharpe.toFixed(2) : "-"}
                  </td>
                </tr>
                <tr><td style={{ padding: "1px 4px" }}>Max DD</td>
                  <td className={"num mono " + (perf ? (perf.drawdown > 1000 ? "bad" : perf.drawdown > 500 ? "warn" : "good") : "")} style={{ padding: "1px 4px" }}>
                    {perf ? "$" + perf.drawdown.toFixed(0) : "-"}
                  </td>
                  <td className={"num mono " + (btResult ? (btResult.metrics.max_dd > 2000 ? "bad" : btResult.metrics.max_dd > 1000 ? "warn" : "good") : "")} style={{ padding: "1px 4px" }}>
                    {btResult ? "$" + btResult.metrics.max_dd.toFixed(0) : "-"}
                  </td>
                </tr>
                <tr><td style={{ padding: "1px 4px" }}>Avg Trade</td>
                  <td className={"num mono " + (liveAvgTrade ? (liveAvgTrade > 0 ? "good" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {liveAvgTrade ? "$" + liveAvgTrade.toFixed(0) : "-"}
                  </td>
                  <td className="num mono" style={{ padding: "1px 4px" }}>{btResult && btResult.metrics.n_trades > 0 ? "$" + (btResult.metrics.pnl / btResult.metrics.n_trades).toFixed(0) : btResult ? "$0" : "-"}</td>
                </tr>
                <tr><td style={{ padding: "1px 4px" }}>Win Rate</td>
                  <td className="num mono" style={{ padding: "1px 4px" }}>{perf ? (perf.win_rate * 100).toFixed(0) + "%" : "-"}</td>
                  <td className="num mono" style={{ padding: "1px 4px" }}>{btResult ? (btResult.metrics.win_rate * 100).toFixed(0) + "%" : "-"}</td>
                </tr>
                <tr><td style={{ padding: "1px 4px" }}>Profit Factor</td>
                  <td className={"num mono " + (perf && perf.profit_factor ? (perf.profit_factor > 1.5 ? "good" : perf.profit_factor > 1 ? "warn" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {perf?.profit_factor?.toFixed(2) ?? "-"}
                  </td>
                  <td className={"num mono " + (btResult && btResult.metrics.pf != null ? (btResult.metrics.pf > 1.5 ? "good" : btResult.metrics.pf > 1 ? "warn" : "bad") : "")} style={{ padding: "1px 4px" }}>
                    {btResult && btResult.metrics.pf != null ? btResult.metrics.pf.toFixed(2) : btResult ? "0.00" : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
              {btResult && btSeries.length > 0 && (
                <div style={{ marginTop: 4, height: 60 }}>
                  <svg width="100%" height="60" viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: "block" }}>
                    <polyline fill="none" stroke="var(--accent)" strokeWidth="1.5" points={btPoints} />
                  </svg>
                  <div className="muted" style={{ fontSize: 7, textAlign: "right" }}>Backtest equity curve</div>
                </div>
              )}
          </div>
          </div>

        <div className="card" style={{ flex: 2, display: "flex", flexDirection: "column", minHeight: 0, marginBottom: 0, overflow: "hidden" }}>
          <h2 style={{ flexShrink: 0 }}>
            Price Chart
            <span className="muted" style={{ fontSize: 8, fontWeight: 400, textTransform: "none", letterSpacing: "0.02em", marginLeft: 6 }}>
              {algo.market?.toUpperCase() + " \u00b7 1m"}
            </span>
          </h2>
          <div style={{ flex: 1, minHeight: 0 }}>
            <OHLCChart bars={bars ?? []} loading={barsLoading} />
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flex: 1.4, gap: 6, minHeight: 0, flexShrink: 0 }}>
        <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", marginBottom: 0, minHeight: 0 }}>
          <h2 style={{ flexShrink: 0 }}>Account Links</h2>
          <div style={{ overflow: "visible" }}>
            {links && links.length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
                <thead>
                  <tr><th style={{ padding: "1px 4px", fontSize: 8 }}>Account</th><th className="num" style={{ padding: "1px 4px", fontSize: 8 }}>Qty</th><th style={{ padding: "1px 4px", fontSize: 8 }}>Status</th></tr>
                </thead>
                <tbody>
                  {links.map(l => (
                    <tr key={l.account_id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td className="mono" style={{ padding: "2px 4px", fontSize: 9 }}>{"#" + l.account_id}</td>
                      <td className="num mono" style={{ padding: "2px 4px", fontSize: 9 }}>{l.contracts}</td>
                      <td style={{ padding: "2px 4px", fontSize: 9 }}><span className={"pill " + (l.active ? "good" : "dim")} style={{ fontSize: 8 }}>{l.active ? "ACTIVE" : "PAUSED"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="muted" style={{ fontSize: 9 }}>Not linked to any account</div>
            )}
          </div>
        </div>

        <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", marginBottom: 0, minHeight: 0 }}>
          <h2 style={{ flexShrink: 0 }}>Runner Log <span className="muted" style={{ fontSize: 8, fontWeight: 400 }}>recent decisions</span></h2>
          <div style={{ overflow: "visible" }}>
            {evals.length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
                <thead>
                  <tr><th style={{ padding: "1px 4px", fontSize: 8 }}>Time</th><th style={{ padding: "1px 4px", fontSize: 8 }}>State</th><th style={{ padding: "1px 4px", fontSize: 8 }}>Note</th></tr>
                </thead>
                <tbody>
                  {evals.slice(-30).reverse().map((e, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td className="mono" style={{ padding: "2px 4px", fontSize: 8, color: "var(--fg-muted)" }}>{e.ts?.slice(5, 19) ?? "-"}</td>
                      <td style={{ padding: "2px 4px" }}>
                        <span className="pill" style={{ fontSize: 7, backgroundColor: e.state === "ready" ? "var(--good)22" : e.state === "watching" ? "var(--warn)22" : "var(--bad)22", color: e.state === "ready" ? "var(--good)" : e.state === "watching" ? "var(--warn)" : "var(--bad)" }}>{e.state}</span>
                      </td>
                      <td style={{ padding: "2px 4px", fontSize: 8, color: "var(--fg-secondary)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.note ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="muted" style={{ fontSize: 9 }}>No runner decisions yet</div>
            )}
          </div>
        </div>

        <div className="card" style={{ flex: 2, display: "flex", flexDirection: "column", marginBottom: 0, minHeight: 0 }}>
          <h2 style={{ flexShrink: 0 }}>Recent Trades</h2>
          <div style={{ overflow: "visible" }}>
            {tradeJournal && tradeJournal.length > 0 ? (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
                <thead>
                  <tr><th style={{ padding: "1px 4px", fontSize: 8 }}>Date</th><th style={{ padding: "1px 4px", fontSize: 8 }}>Side</th><th className="num" style={{ padding: "1px 4px", fontSize: 8 }}>Qty</th><th className="num" style={{ padding: "1px 4px", fontSize: 8 }}>Entry</th><th className="num" style={{ padding: "1px 4px", fontSize: 8 }}>Exit</th><th className="num" style={{ padding: "1px 4px", fontSize: 8 }}>PnL</th><th style={{ padding: "1px 4px", fontSize: 8 }}>Reason</th></tr>
                </thead>
                <tbody>
                  {tradeJournal.slice(0, 15).map(t => (
                    <tr key={t.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td className="mono" style={{ padding: "2px 4px", fontSize: 9 }} title={t.entry_ts}>{t.entry_ts?.slice(0, 10)}</td>
                      <td style={{ padding: "2px 4px", color: (t.side === "Buy" || t.side === "long") ? "var(--good)" : "var(--bad)", fontWeight: 600 }}>
                        {(t.side === "Buy" || t.side === "long") ? "LONG" : "SHORT"}
                      </td>
                      <td className="num mono" style={{ padding: "2px 4px", fontSize: 9 }}>{t.qty}</td>
                      <td className="num mono" style={{ padding: "2px 4px", fontSize: 9 }}>{"$" + t.entry_price?.toFixed(2)}</td>
                      <td className="num mono" style={{ padding: "2px 4px", fontSize: 9 }}>{t.exit_price != null ? "$" + t.exit_price.toFixed(2) : "-"}</td>
                      <td className={"num mono " + ((t.pnl_net ?? 0) >= 0 ? "good" : "bad")} style={{ padding: "2px 4px", fontSize: 9 }}>
                        {t.pnl_net != null ? "$" + t.pnl_net.toFixed(0) : t.pnl_gross != null ? "$" + t.pnl_gross.toFixed(0) : "-"}
                      </td>
                      <td style={{ padding: "2px 4px", fontSize: 8, color: "var(--fg-muted)" }}>{t.exit_reason ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="muted" style={{ fontSize: 9 }}>No trades yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}